import { t } from '../../../i18next';
import { TableRes } from '../../../shared/types';
import PspConflictTable from '../components/PspConflictTable';
import { getPspRecordsByKey } from './getPspRecordsActive';
import { deactivateExistingPspRecords } from './pspBeforeCommit';
import { getAllDropdowns } from '../../../shared/services/allDropdownsRegistry';
import { getActiveValue, isActiveEqual } from './getActiveValue';

export type RowsProps = {
  add: any[];
  edit: any[];
  editRowOriginal: Record<string, any>;
};

export type ConfirmBeforeCommitResult = {
  shouldOpenConfirmation: boolean;
  confirmationProps?: {
    title?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    children?: React.ReactNode;
    beforeCommit?: () => Promise<void>;
  };
};

// find label from valueOptions for singleSelect columns
function getSingleSelectLabel(columns: any[] | undefined, field: string, value: any) {
  const col = columns?.find((c) => c?.attrInternalName === field || c?.field === field);
  if (!col || !Array.isArray(col.valueOptions)) return value;
  const match = col.valueOptions.find((opt: any) => String(opt?.value) === String(value));
  return match?.label ?? value;
}

function buildKey(row: any) {
  return {
    designOwnerId: row?.design_owner_id ?? '',
    pfLc: row?.pf_lc ?? '',
    mpNr: (row?.mp_nr ?? '').toString().trim(),
  };
}

function formatKeyLabel(key: { designOwnerId: any; pfLc: any; mpNr: any }, columns?: any[]) {
  const ownerLabel = getSingleSelectLabel(columns, 'design_owner_id', key.designOwnerId);
  const pfLabel = getSingleSelectLabel(columns, 'pf_lc', key.pfLc);
  return `${ownerLabel} , ${pfLabel} , ${key.mpNr}`;
}

export async function PspConfirmBeforeCommit(
  rowsProps: RowsProps,
  tableDetails: TableRes, 
  _accessData: { accessDetails: any; roleIds: any[] },
  allAccessibleColumns: any[],
): Promise<ConfirmBeforeCommitResult> {
  const screenId = tableDetails?.id;
  const schemaName = tableDetails?.mbdDataGrid.schemaName;
  const tableName = tableDetails?.mbdDataGrid?.dataGridSource ?? 'psptable';
  const addedRows = Array.isArray(rowsProps?.add) ? rowsProps.add : [];

  if (!screenId || addedRows.length === 0) {
    return { shouldOpenConfirmation: false };
  }

  const allDropdowns = getAllDropdowns();
  const { yes: ACTIVE_YES } = getActiveValue(allDropdowns?.active || []);

  // Collect unique PSP keys from added rows
  const keys = addedRows.map(buildKey).filter((k) => k.designOwnerId && k.pfLc && k.mpNr);

  if (keys.length === 0) {
    return { shouldOpenConfirmation: false };
  }

  // Query active existing records for each key
  const confirmations: Array<{ keyLabel: string; ids: Array<string | number> }> = [];
  const idsToDeactivate: number[] = [];

  for (const key of keys) {
    try {
      const data = await getPspRecordsByKey(key.designOwnerId, key.pfLc, key.mpNr, screenId);

      const rows: Array<any> = data?.[`${schemaName}_${tableName}`] ?? [];
      const activeRows = rows.filter((r) => isActiveEqual(r?.active, ACTIVE_YES));

      if (activeRows.length > 0) {
        const ids = activeRows.map((r) => r?.id).filter((id) => id !== undefined);
        confirmations.push({
          keyLabel: formatKeyLabel(key, allAccessibleColumns),
          ids,
        });
        idsToDeactivate.push(...ids.filter((id) => typeof id === 'number'));
      }
    } catch {}
  }

  const shouldOpenConfirmation = confirmations.length > 0;

  if (!shouldOpenConfirmation) {
    return { shouldOpenConfirmation: false };
  }

  const Message = () => (
    <div>
      <div>{t('psp_management.same_psp_key_records_found')}</div>
      <PspConflictTable rows={confirmations} />
      <div>{t('psp_management.deactivate_existing_confirm')}</div>
    </div>
  );

  // Deactivate existing active records
  const beforeCommit = async (): Promise<void> => {
    if (!idsToDeactivate.length) return;
    await deactivateExistingPspRecords({
      screenId,
      schemaName,
      tableName,
      idsToDeactivate,
    });
  };

  return {
    shouldOpenConfirmation,
    confirmationProps: {
      title: 'psp_management.deactivate_existing_title',
      confirmLabel: 'save',
      cancelLabel: 'cancel',
      children: <Message />,
      beforeCommit,
    },
  };
}
